<template>
  <el-dialog
    v-model="dialogVisible"
    :close-on-click-modal="false"
    title="商品分销配置"
    width="520px"
    @closed="handleClosed"
  >
    <el-form ref="formRef" v-loading="formLoading" :model="formData" label-width="130px">
      <el-form-item label="商品名称">
        <span class="text-ellipsis">{{ currentRow?.name || '-' }}</span>
      </el-form-item>
      <el-form-item label="区域代理分红">
        <el-switch v-model="formData.areaAgentEnabled" />
        <span class="ml-2 text-12 text-[var(--el-text-color-secondary)]">
          开启后按区域代理规则分红
        </span>
      </el-form-item>
      <el-form-item label="商品返现">
        <el-switch v-model="formData.cashbackEnabled" />
        <span class="ml-2 text-12 text-[var(--el-text-color-secondary)]">
          开启后按总返现与日释放比例逐日释放
        </span>
      </el-form-item>
      <template v-if="formData.cashbackEnabled">
        <el-form-item label="单件总返现">
          <el-input-number
            v-model="formData.cashbackTotalPrice"
            :min="0"
            :precision="0"
            :step="100"
            class="!w-200px"
          />
          <span class="ml-2 text-12 text-[var(--el-text-color-secondary)]">单位：分</span>
        </el-form-item>
        <el-form-item label="日释放比例">
          <el-input-number
            v-model="formData.cashbackDailyRate"
            :min="0"
            :max="10000"
            :precision="0"
            :step="100"
            class="!w-200px"
          />
          <span class="ml-2 text-12 text-[var(--el-text-color-secondary)]">
            万分比，1000 = 10%
          </span>
        </el-form-item>
      </template>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button :loading="formLoading" type="primary" @click="submitForm">确 定</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { getSpuDistConfig, saveSpuDistConfig } from '@/api/distribution/spuConfig'

defineOptions({ name: 'SpuDistConfigDialog' })

const emit = defineEmits<{ success: [] }>()

const message = useMessage()
const dialogVisible = ref(false)
const formLoading = ref(false)
const currentRow = ref<any>()

const formData = ref({
  spuId: undefined as number | string | undefined,
  areaAgentEnabled: false,
  cashbackEnabled: false,
  cashbackTotalPrice: undefined as number | undefined,
  cashbackDailyRate: undefined as number | undefined
})

/** 打开弹窗：与 AuditDialog 保持同一风格，由父组件传当前行 */
const open = async (row: any) => {
  currentRow.value = row
  dialogVisible.value = true
  formLoading.value = true
  try {
    const data = await getSpuDistConfig(row.id)
    formData.value = {
      spuId: row.id,
      areaAgentEnabled: data?.areaAgentEnabled ?? false,
      cashbackEnabled: data?.cashbackEnabled ?? false,
      cashbackTotalPrice: data?.cashbackTotalPrice ?? undefined,
      cashbackDailyRate: data?.cashbackDailyRate ?? undefined
    }
  } finally {
    formLoading.value = false
  }
}

const submitForm = async () => {
  // 返现开启时两项必填（后端也有校验，这里先拦一层给出更明确提示）
  if (formData.value.cashbackEnabled) {
    if (!formData.value.cashbackTotalPrice) {
      message.warning('开启返现时，请填写单件总返现')
      return
    }
    if (!formData.value.cashbackDailyRate) {
      message.warning('开启返现时，请填写日释放比例')
      return
    }
  }
  formLoading.value = true
  try {
    await saveSpuDistConfig({
      spuId: formData.value.spuId,
      areaAgentEnabled: formData.value.areaAgentEnabled,
      cashbackEnabled: formData.value.cashbackEnabled,
      cashbackTotalPrice: formData.value.cashbackEnabled
        ? formData.value.cashbackTotalPrice
        : undefined,
      cashbackDailyRate: formData.value.cashbackEnabled
        ? formData.value.cashbackDailyRate
        : undefined
    })
    message.success('保存成功')
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 关闭复位，避免下次打开短暂显示上次数据 */
const handleClosed = () => {
  currentRow.value = undefined
  formData.value = {
    spuId: undefined,
    areaAgentEnabled: false,
    cashbackEnabled: false,
    cashbackTotalPrice: undefined,
    cashbackDailyRate: undefined
  }
}

defineExpose({ open })
</script>

<style scoped>
.text-ellipsis {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
